import * as PIXI from "pixi.js";
import { AssetLoader } from "src/core/assets/AssetLoader";
import { Audio, registerSound } from "../audio/Audio";

export type TAssetBundle = string;
export type TAssetAlias = string;
export type ProgressCallback = (progress: number) => void;

export enum EAssetType {
  SPRITESHEET,
  SPINE_ATLAS,
  BITMAP_FONT,
  DOCUMENT_FONT,
  IMAGE,
  JSON,
  AUDIO
}

export type TAssetData = {
  bundle: TAssetBundle;
  src: string;
  type: EAssetType;
  imageSrc?: string | string[];
};

// Pixi Assets wrapper to track progress and handle custom assets
// Loading Screen-s results in loading the assets associated with them
// Generally speaking, this class is unnecessary to use outside of Screen.ts
export class Assets {
  // Handles loading different asset types and deals with cache-busted assets efficiently
  public static async loadAssets(
    assets: Record<TAssetAlias, TAssetData>,
    progressCallback?: ProgressCallback,
  ): Promise<void> {
    const assetEntries: [TAssetAlias, TAssetData][] = Object.entries(assets);
    const assetCount: number = assetEntries.length;
    let loadedAssetCount: number = 0;

    const assetPromises = assetEntries.map(async ([alias, asset]) => {
      try {
        switch (asset.type) {
          case EAssetType.SPRITESHEET:
            await AssetLoader.createSpritesheet([alias, asset]);
            break;
          case EAssetType.SPINE_ATLAS:
            await AssetLoader.createSpineAtlas([alias, asset]);
            break;
          case EAssetType.DOCUMENT_FONT:
            await AssetLoader.loadDocumentFont([alias, asset]);
            break;
          case EAssetType.BITMAP_FONT:
            await AssetLoader.createBitmapFont([alias, asset]);
            break;
          case EAssetType.AUDIO:
            await new Promise<void>((resolve, reject) => {
              try {
                // 1. Register with our Howler wrapper
                registerSound(alias, asset.src);

                const howl = Audio.get(alias);

                if (howl) {
                  // 2. Wait for the audio to be loaded/buffered before resolving
                  if (howl.state() === 'loaded') {
                    resolve();
                  } else {
                    howl.once('load', () => resolve());
                    howl.once('loaderror', (_, err) => reject(err));
                  }
                } else {
                  resolve(); // Fallback if registration failed
                }
              } catch (e) {
                reject(e);
              }
            });
            break
          case EAssetType.IMAGE:
          case EAssetType.JSON:
            await PIXI.Assets.load({ src: asset.src, alias: alias });
            break;
        }
      } catch (error) {
        throw new Error(`Failed to load asset ${alias}: ${error}`);
      }

      // console.log(`Loaded asset ${alias}`);
      // console.log(asset);

      loadedAssetCount++;
      progressCallback?.(loadedAssetCount / assetCount);
    });
    await Promise.all(assetPromises);
  }

  // Unloads regular and custom loaded pixi assets
  public static async unloadAssets(
    assets: Record<TAssetAlias, TAssetData>,
  ): Promise<void> {
    const assetEntries: [TAssetAlias, TAssetData][] = Object.entries(assets);

    assetEntries.forEach(([alias, asset]) => {
      try {
        if (asset.type === EAssetType.DOCUMENT_FONT || asset.type === EAssetType.BITMAP_FONT) {
          console.log(`Skipping font unload: ${alias} (keeping fonts loaded)`);
          return;
        }

        // Unload other assets normally
        const cachedAsset = PIXI.Assets.cache.get(alias);
        if (cachedAsset) {
          if (asset.imageSrc || asset.src.includes(".fnt")) {
            PIXI.Assets.unload(alias);
            console.log(`Unloaded asset ${alias}`);
          } else {
            // Standard PIXI loading, unload by src
            PIXI.Assets.unload(asset.src);
            console.log(`Unloaded asset ${alias}`);
          }
        }
      } catch (error) {
        console.warn(`Failed to unload asset ${alias}:`, error);
      }
    });
  }

  public static getAsset<K extends TAssetAlias>(key: K): any {
    const asset = PIXI.Assets.get(key as string);
    if (!asset) {
      throw new Error(`Asset ${String(key)} not available`);
    }
    return asset;
  }
}
